"use client";
import React from "react";
import { Box, Typography, Container } from "@mui/material";
import { useTranslation } from "react-i18next";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import Groups2Icon from "@mui/icons-material/Groups2";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";

// Single red accent theme across all stats, matching the reference design
const RED = "#F7222C";
const RED_DARK = "#F7222C";

// Order here is the visual grid order (top-left, top-right, bottom-left, bottom-right),
// NOT the stat1/2/3/4 key order — stat2 and stat3 are swapped on screen vs. the i18n keys.
const stats = [
  {
    Icon: AccessTimeIcon,
    hoursKey: "timeBreakdownSection.stat1.hours",
    labelKey: "timeBreakdownSection.stat1.label",
    value: 23, // used only to size the progress bar relative to the others
  },
  {
    Icon: PeopleAltIcon,
    hoursKey: "timeBreakdownSection.stat3.hours",
    labelKey: "timeBreakdownSection.stat3.label",
    value: 5,
  },
  {
    Icon: CalendarMonthIcon,
    hoursKey: "timeBreakdownSection.stat2.hours",
    labelKey: "timeBreakdownSection.stat2.label",
    value: 8,
  },
  {
    Icon: MailOutlineIcon,
    hoursKey: "timeBreakdownSection.stat4.hours",
    labelKey: "timeBreakdownSection.stat4.label",
    value: 12,
  },
];

const MAX_VALUE = Math.max(...stats.map((s) => s.value));

const TimeBreakdownSection = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const UI_BOX_SHADOW =
    "0px 15px 35px rgba(0, 0, 0, 0.04), 0px 5px 15px rgba(0, 0, 0, 0.02)";

  return (
    <Box
      sx={{
        background:
          'url("https://getgeek-website-assets.b-cdn.net/Getgeek%20Images/image%20(32).webp") center center / cover no-repeat',
        py: { xs: 2, sm: 7, md: 10 },
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        {/* ── HEADER ── */}
        <Box sx={{ textAlign: "center", mb: { xs: 4, sm: 5, md: 7 } }}>
          {/* Badge */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.8,
              backgroundColor: "#F3E8FF",
              borderRadius: "50px",
              px: { xs: 1.5, sm: 1.8 },
              py: 0.9,
              mb: { xs: 2, sm: 3 },
            }}
          >
            <AccessTimeIcon
              sx={{
                fontSize: { xs: "0.8rem", sm: "1rem" },
                color: "#8200DB",
              }}
            />
            <Typography
              sx={{
                fontFamily: "var(--font-outfit)",
                fontSize: { xs: "0.7rem", sm: "0.9rem" },
                fontWeight: 500,
                color: "#8200DB",
                letterSpacing: "0.05em",
                textTransform: "uppercase",
              }}
            >
              {t("timeBreakdownSection.badge")}
            </Typography>
          </Box>

          {/* Heading */}
          <Typography
            component="h2"
            sx={{
              fontFamily: "var(--font-plusjakarta)",
              fontWeight: 800,
              fontSize: { xs: "1.75rem", sm: "2.4rem", md: "3.1rem" },
              lineHeight: { xs: 1.2, md: 1.3 },
              color: "#101828",
              mb: { xs: 1.5, sm: 2 },
              maxWidth: 760,
              mx: "auto",
              letterSpacing: "-0.02em",
            }}
          >
            {t("timeBreakdownSection.heading")}
          </Typography>

          {/* Subtext */}
          <Typography
            sx={{
              fontFamily: "var(--font-outfit)",
              fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1.05rem" },
              color: "#364153",
              lineHeight: 1.6,
              px: { xs: 1, sm: 0 },
            }}
          >
            {t("timeBreakdownSection.subtext")}
          </Typography>
        </Box>

        {/* ── SPENDING LABEL ── */}
        <Typography
          sx={{
            fontFamily: "var(--font-outfit)",
            fontSize: { xs: "0.85rem", sm: "0.98rem" },
            fontWeight: 500,
            color: "#7A8598",
            mb: { xs: 2, sm: 2.5 },
            textAlign: "center",
          }}
        >
          {t("timeBreakdownSection.spendingLabel")}
        </Typography>

        {/* ── 2x2 STAT GRID ── */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: { xs: 2, sm: 2.5 },
            mb: { xs: 2.5, sm: 3 },
          }}
        >
          {stats.map((stat, i) => {
            const Icon = stat.Icon;
            const widthPct = "100%";

            return (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 1.5, sm: 2 },
                  backgroundColor: "#fff",
                  boxShadow: UI_BOX_SHADOW,
                  border: "1px solid rgba(241, 245, 249, 0.8)",
                  borderRadius: "18px",
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 1.5, sm: 2 },
                }}
              >
                {/* Icon */}
                <Box
                  sx={{
                    width: { xs: 44, sm: 52 },
                    height: { xs: 44, sm: 52 },
                    borderRadius: "14px",
                    backgroundColor: RED,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Icon sx={{ color: "#fff", fontSize: { xs: 22, sm: 26 } }} />
                </Box>

                {/* Number + label + bar */}
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.6,
                    minWidth: 0,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "var(--font-outfit)",
                      fontSize: { xs: "0.78rem", sm: "0.92rem" },
                      color: "#6A7282",
                      lineHeight: 1.3,
                      fontWeight: 500,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: "1.3rem", sm: "1.7rem" },
                        color: RED,
                        mr: 0.8,
                      }}
                    >
                      {t(stat.hoursKey)}
                    </Box>
                    {t(stat.labelKey)}
                  </Typography>

                  <Box
                    sx={{
                      height: { xs: 6, sm: 8 },
                      borderRadius: 5,
                      backgroundColor: "#f8fafc",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        width: widthPct,
                        height: "100%",
                        borderRadius: 5,
                        background: `linear-gradient(90deg, ${RED} 0%, ${RED_DARK} 100%)`,
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
        <div id="footer-trigger-sentinel" style={{ height: 0 }} />

        {/* ── 48 HOURS BANNER ── */}
        <Box
          sx={{
            background: "linear-gradient(100deg, #FFFBFB 0%, #FFE2E2 90%)",
            border: "1px solid #FFC9C9",
            boxShadow: UI_BOX_SHADOW,
            borderRadius: "20px",
            px: { xs: 2.5, sm: 4 },
            py: { xs: 2.5, sm: 3 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "nowrap",
            flexWrap: "wrap",
            gap: { xs: 1.2, sm: 2 },
            mb: { xs: 4, sm: 5, md: 6 },
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: { xs: 40, sm: 48 },
              height: { xs: 40, sm: 48 },
              borderRadius: "14px",
              backgroundColor: RED,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <AccessTimeIcon
              sx={{ color: "#fff", fontSize: { xs: 22, sm: 26 } }}
            />
          </Box>

          <Typography
            sx={{
              fontFamily: "var(--font-outfit)",
              fontWeight: 800,
              fontSize: { xs: "1.8rem", sm: "2.4rem" },
              color: "#101828",
              lineHeight: 1,
            }}
          >
            {t("timeBreakdownSection.card1.number")}{" "}
            <Box
              component="span"
              sx={{ fontSize: { xs: "1rem", sm: "1.2rem" }, fontWeight: 800 }}
            >
              {t("timeBreakdownSection.card1.unit")}
            </Box>{" "}
            <Box
              component="span"
              sx={{
                fontSize: { xs: "0.78rem", sm: "0.92rem" },
                color: "#6A7282",
                fontWeight: 500,
              }}
            >
              {t("timeBreakdownSection.card1.desc")}
            </Box>
          </Typography>
        </Box>

        {/* ── BOTTOM CARD ── */}
        <Box
          sx={{
            backgroundColor: "#fff",
            border: "1px solid #E5E7EB",
            boxShadow: UI_BOX_SHADOW,
            borderRadius: "24px",
            p: { xs: 3, sm: 4, md: 5 },
            textAlign: "center",
            maxWidth: 800,
            mx: "auto",
          }}
        >
          <Typography
            sx={{
              fontFamily: "var(--font-outfit)",
              fontWeight: 400,
              fontSize: { xs: "1rem", sm: "1.15rem", md: "1.4rem" },
              color: "#101828",
              mb: { xs: 1.5, sm: 2 },
              lineHeight: 1.4,
            }}
          >
            {t("timeBreakdownSection.bottomCard.line1Before")}{" "}
            <Box component="span" sx={{ color: "#9810FA", fontWeight: 500 }}>
              {t("timeBreakdownSection.bottomCard.line1Accent")}
            </Box>
          </Typography>

          {/* Single flowing paragraph — no divider, matches reference */}
          <Typography
            sx={{
              fontFamily: "var(--font-outfit)",
              fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
              color: "#424E61",
              lineHeight: 1.7,
              fontWeight: 400,
            }}
          >
            <Box component="strong" sx={{ fontWeight: 600, color: "#424E61" }}>
              {t("timeBreakdownSection.bottomCard.line2")}
            </Box>{" "}
            {t("timeBreakdownSection.bottomCard.line3Before")}
            {t("timeBreakdownSection.bottomCard.line3Bold") && (
              <>
                {" "}
                <Box
                  component="strong"
                  sx={{ fontWeight: 600, color: "#101828" }}
                >
                  {t("timeBreakdownSection.bottomCard.line3Bold")}
                </Box>
              </>
            )}
            {t("timeBreakdownSection.bottomCard.line3After") && (
              <> {t("timeBreakdownSection.bottomCard.line3After")}</>
            )}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default TimeBreakdownSection;
